import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Stack,
} from '@mui/material';
import {
  Home as HomeIcon,
  Description as DescriptionIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuthenticatedFetch } from '../../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../../utils/config';
import { NoteIcon } from '../../../components/accounting/NoteIcon';

interface Bill {
  id: string;
  vendorId?: string;
  vendorName: string;
  totalAmount: number;
  balanceDue: number;
  status: 'open' | 'paid' | 'void';
  createdTime: string;
  currencyCode?: string;
  hasNotes?: boolean;
}

export const BillManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [bills, setBills] = useState<Bill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const isFetchingRef = useRef(false);
  
  const fetchBills = useCallback(async () => {
    // Prevent concurrent requests
    if (isFetchingRef.current) {
      return;
    }
    
    isFetchingRef.current = true;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        offset: (page * rowsPerPage).toString(),
        limit: rowsPerPage.toString(),
      });
      
      const response = await authenticatedFetch(
        `${API_URL}/api/accounting/admin/bills?${params.toString()}`
      );
      
      if (!response || !response.ok) {
        throw new Error('Failed to fetch bills');
      }
      
      const data = await response.json();
      setBills(data.data?.bills || []);
      setTotalCount(data.data?.pagination?.total || 0);
      setError(null);
    } catch (err: any) {
      // Don't set error if it's an abort error
      if (err?.name !== 'AbortError') {
        console.error('Error fetching bills:', err);
        setError('Failed to fetch bills');
      }
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [page, rowsPerPage, authenticatedFetch]);
  
  useEffect(() => {
    fetchBills();
  }, [fetchBills]);
  
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };
  
  const formatCurrency = (amount?: number, currencyCode?: string) => {
    if (amount === undefined || amount === null) return '-';
    return `${currencyCode || 'USD'} ${amount.toFixed(2)}`;
  };

  const getStatusChipProps = (status: string) => {
    switch (status) {
      case 'paid':
        return { color: 'success' as const, label: 'Paid' };
      case 'void':
        return { color: 'default' as const, label: 'Void' };
      case 'open':
      default:
        return { color: 'warning' as const, label: 'Open' };
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
          Home
        </Link>
        <Link
          color="inherit"
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate('/admin/hub')}
        >
          Admin Hub
        </Link>
        <Typography color="text.primary">Bill Management</Typography>
      </Breadcrumbs>
      
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <DescriptionIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Bill Management
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          View and manage all QuickBooks bills across vendors.
        </Typography>
      </Box>
      
      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {/* Actions Bar */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            size="small"
            placeholder="Search bills..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ flexGrow: 1, maxWidth: 400 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            disabled={loading}
          />
          <IconButton onClick={fetchBills} title="Refresh" disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Stack>
      </Paper>
      
      {/* Bills Table */}
      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : bills.length === 0 ? (
          <Alert severity="info" sx={{ m: 2 }}>
            No bills found.
          </Alert>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={40}></TableCell>
                  <TableCell>QuickBooks ID</TableCell>
                  <TableCell>Vendor</TableCell>
                  <TableCell align="right">Total Amount</TableCell>
                  <TableCell align="right">Balance</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bills.map((bill) => (
                  <TableRow
                    key={bill.id}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                    onClick={() => navigate(`/admin/hub/accounting/bills/${bill.id}`)}
                  >
                    <TableCell sx={{ px: 1 }}>
                      <NoteIcon hasNotes={bill.hasNotes || false} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium" sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                        {bill.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {bill.vendorId ? (
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() => navigate(`/admin/hub/accounting/vendors/${bill.vendorId}`)}
                          sx={{
                            textAlign: 'left',
                            textDecoration: 'none',
                            '&:hover': {
                              textDecoration: 'underline',
                            }
                          }}
                        >
                          {bill.vendorName || '-'}
                        </Link>
                      ) : (
                        <Typography variant="body2">
                          {bill.vendorName || '-'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatCurrency(bill.totalAmount, bill.currencyCode)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color={bill.balanceDue > 0 ? 'error' : 'text.primary'}>
                        {formatCurrency(bill.balanceDue, bill.currencyCode)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(bill.createdTime)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusChipProps(bill.status).label}
                        color={getStatusChipProps(bill.status).color}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </TableContainer>
    </Container>
  );
};